import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { Add } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { Portal } from '../internal/Portal';
import { useDialogBehavior } from '../internal/useDialogBehavior';
import styles from './Drawer.module.css';

export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: DrawerSize;
  side?: 'right' | 'left';
  /** Close when the backdrop is clicked. Default true. */
  dismissible?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  className?: string;
  bodyClassName?: string;
}

/** Slide-over panel (a modal dialog anchored to the viewport edge). */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  side = 'right',
  dismissible = true,
  initialFocusRef,
  closeLabel = 'Close panel',
  className,
  bodyClassName,
}: DrawerProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useDialogBehavior({
    open,
    containerRef: panelRef,
    initialFocusRef,
    onEscape: onClose,
    lockScroll: true,
  });

  if (!open) return null;

  return (
    <Portal>
      <div
        className={styles.backdrop}
        role="presentation"
        onMouseDown={(event) => {
          if (dismissible && event.target === event.currentTarget) onClose();
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
          className={cx(styles.panel, styles[size], styles[side], className)}
        >
          <div className={styles.header}>
            <div className={styles.headings}>
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className={styles.description}>
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              className={styles.close}
              aria-label={closeLabel}
              onClick={onClose}
              data-skip-initial-focus
            >
              <Add size={22} color="currentColor" className={styles.closeIcon} aria-hidden="true" />
            </button>
          </div>
          <div className={cx(styles.body, bodyClassName)}>{children}</div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}
